import Link from "next/link";
import type {
  CCompanyDigitalizationContent,
  CCompanyDigitalizationItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cCompanyDigitalizationTranslations } from "./CCompanyDigitalization.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CCompanyDigitalization.module.css";

/**
 * CCompanyDigitalization - visitor view komponentu `c_dedicated_company_digitalization`.
 *
 * Data source: `C_DEDICATED_COMPANY_DIGITALIZATION_CONTROLLER::getApiContent()` zwraca:
 *   { id, symbol, hero: {eyebrow, title, description}, items: [...] }
 *
 * Renderuje:
 *   - Hero section: full-bleed light bg + centered eyebrow + big H1 + description
 *   - Grid kart katalogow potomnych (icon + name + description + "Dowiedz sie wiecej ->")
 *
 * Wzorowany na CNestedCatalogs (grid siatki) rozszerzony o hero + description per karta.
 */
export function CCompanyDigitalization({ content }: CCompanyDigitalizationProps) {
  const t = useT(cCompanyDigitalizationTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  const items: CCompanyDigitalizationItem[] = Array.isArray(content.items)
    ? content.items
    : [];
  const hero = content.hero ?? { eyebrow: null, title: null, description: null };

  const hasHero =
    (hero.eyebrow && hero.eyebrow.trim() !== "") ||
    (hero.title && hero.title.trim() !== "") ||
    (hero.description && hero.description.trim() !== "");

  return (
    <div className={styles.root}>
      {hasHero ? (
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            {hero.eyebrow ? (
              <p className={styles.heroEyebrow}>{hero.eyebrow}</p>
            ) : null}
            {hero.title ? <h1 className={styles.heroTitle}>{hero.title}</h1> : null}
            {hero.description ? (
              <p className={styles.heroDescription}>{hero.description}</p>
            ) : null}
          </div>
        </section>
      ) : null}

      {items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <ul className={styles.cards}>
          {items.map((item) => (
            <CatalogCard key={item.id} item={item} readMoreLabel={t.readMore} />
          ))}
        </ul>
      )}

      {content.integrations ? (
        <section className={styles.integrations}>
          <header className={styles.integrationsHeader}>
            <h2 className={styles.integrationsTitle}>
              {content.integrations.icon ? (
                <span className={styles.integrationsIcon} aria-hidden="true">
                  <i className={`nc-icon-outline ${content.integrations.icon}`} />
                </span>
              ) : null}
              {content.integrations.title}
            </h2>
            {content.integrations.description ? (
              <p className={styles.integrationsDescription}>
                {content.integrations.description}
              </p>
            ) : null}
          </header>
          <ul className={styles.cards}>
            {content.integrations.items.map((item) => (
              <CatalogCard key={item.id} item={item} readMoreLabel={t.readMore} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export type CCompanyDigitalizationProps = {
  content: CCompanyDigitalizationContent;
};

function CatalogCard({
  item,
  readMoreLabel,
}: {
  item: CCompanyDigitalizationItem;
  readMoreLabel: string;
}) {
  const href = extractInternalHref(item.href);

  const inner = (
    <>
      {item.icon ? (
        <span className={styles.cardIconWrap} aria-hidden="true">
          <i className={`nc-icon-outline ${item.icon}`} />
        </span>
      ) : null}
      <div className={styles.cardBody}>
        <h2 className={styles.cardTitle}>{item.name}</h2>
        {item.description ? (
          <p className={styles.cardDesc}>{item.description}</p>
        ) : null}
        <span className={styles.cardCta}>
          {readMoreLabel} <span aria-hidden="true">→</span>
        </span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink}>
          {inner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink}>
          {inner}
        </a>
      )}
    </li>
  );
}
